import React, { useState } from 'react';

/*
 * Small charts of the dashboards, drawn with SVG and CSS without a chart library: columns
 * stacked by series, a donut, horizontal bars and a funnel. The colors come from the
 * caller, so the same status or work type has the same color everywhere.
 */

export interface ISeries {
  color: string;
  key: string;
  label: string;
}

export interface IStackedColumn {
  label: string;
  values: { [seriesKey: string]: number };
}

const sum = (values: number[]): number =>
  values.reduce((total, value) => total + value, 0);

/**
 * Columns stacked by series, e.g. the offered euros of each month by status. Hovering a
 * column shows its parts.
 */
export const StackedColumns = ({
  columns,
  format,
  series,
}: {
  columns: IStackedColumn[];
  format: (value: number) => string;
  series: ISeries[];
}): JSX.Element => {
  const [hovered, setHovered] = useState<null | number>(null);
  const totals = columns.map((column) => sum(Object.values(column.values)));
  const max = Math.max(1, ...totals);

  return (
    <div className="dashboard-columns">
      <div className="dashboard-columns-area">
        {columns.map((column, index) => (
          <div
            className={`dashboard-column${hovered === index ? ' hovered' : ''}`}
            key={column.label}
            onMouseEnter={() => setHovered(index)}
            onMouseLeave={() => setHovered(null)}
          >
            <div className="dashboard-column-total">
              {totals[index] > 0 ? format(totals[index]) : ''}
            </div>
            <div
              className="dashboard-column-stack"
              style={{ height: `${(totals[index] / max) * 100}%` }}
            >
              {series.map((item) =>
                (column.values[item.key] ?? 0) > 0 ? (
                  <div
                    className="dashboard-column-part"
                    key={item.key}
                    style={{
                      background: item.color,
                      flexGrow: column.values[item.key],
                    }}
                    title={`${item.label}: ${format(column.values[item.key])}`}
                  />
                ) : null
              )}
            </div>
            <div className="dashboard-column-label">{column.label}</div>
            {hovered === index && totals[index] > 0 ? (
              <div className="dashboard-tooltip">
                <strong>{column.label}</strong>
                {series
                  .filter((item) => (column.values[item.key] ?? 0) > 0)
                  .map((item) => (
                    <div key={item.key}>
                      <span
                        className="dashboard-dot"
                        style={{ background: item.color }}
                      />
                      {`${item.label}: ${format(column.values[item.key])}`}
                    </div>
                  ))}
              </div>
            ) : null}
          </div>
        ))}
      </div>
      <Legend series={series} />
    </div>
  );
};

export const Legend = ({ series }: { series: ISeries[] }): JSX.Element => (
  <div className="dashboard-legend">
    {series.map((item) => (
      <span key={item.key}>
        <span className="dashboard-dot" style={{ background: item.color }} />
        {item.label}
      </span>
    ))}
  </div>
);

/**
 * A donut of the parts of a whole, e.g. the offers by status, with the total in the middle.
 */
export const Donut = ({
  center,
  centerLabel,
  parts,
  onSelect,
  selected,
}: {
  center: string;
  centerLabel: string;
  parts: {
    color: string;
    key: string;
    label: string;
    value: number;
    text: string;
  }[];
  // Clicking a part chooses it, e.g. as a filter
  onSelect?: (key: string) => void;
  selected?: null | string;
}): JSX.Element => {
  const total = sum(parts.map((part) => part.value));
  const radius = 70;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;

  return (
    <div className="dashboard-donut">
      <svg viewBox="0 0 200 200" role="img" aria-label={centerLabel}>
        <circle
          cx="100"
          cy="100"
          r={radius}
          className="dashboard-donut-track"
        />
        {total > 0
          ? parts
              .filter((part) => part.value > 0)
              .map((part) => {
                const length = (part.value / total) * circumference;
                const circle = (
                  // eslint-disable-next-line jsx-a11y/no-static-element-interactions
                  <circle
                    className={`dashboard-donut-part${
                      selected && selected !== part.key ? ' faded' : ''
                    }`}
                    cx="100"
                    cy="100"
                    key={part.key}
                    onClick={() => onSelect?.(part.key)}
                    r={radius}
                    stroke={part.color}
                    strokeDasharray={`${length} ${circumference - length}`}
                    strokeDashoffset={-offset}
                  >
                    <title>{`${part.label}: ${part.text}`}</title>
                  </circle>
                );
                offset += length;
                return circle;
              })
          : null}
        <text x="100" y="96" className="dashboard-donut-center">
          {center}
        </text>
        <text x="100" y="118" className="dashboard-donut-center-label">
          {centerLabel}
        </text>
      </svg>
      <div className="dashboard-donut-legend">
        {parts.map((part) => (
          <button
            className={`dashboard-donut-legend-item${
              selected === part.key ? ' selected' : ''
            }`}
            key={part.key}
            onClick={() => onSelect?.(part.key)}
            type="button"
          >
            <span
              className="dashboard-dot"
              style={{ background: part.color }}
            />
            <span className="dashboard-donut-legend-label">{part.label}</span>
            <span className="dashboard-donut-legend-value">{part.text}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

/**
 * Horizontal bars, e.g. the offered euros by department. A bar can have a colored badge
 * (e.g. the margin %), and clicking a bar chooses it.
 */
export const BarList = ({
  bars,
  onSelect,
  selected,
}: {
  bars: {
    badge?: { color: string; text: string };
    color: string;
    key: string;
    label: string;
    text: string;
    value: number;
  }[];
  onSelect?: (key: string) => void;
  selected?: null | string;
}): JSX.Element => {
  const max = Math.max(1, ...bars.map((bar) => bar.value));

  return (
    <div className="dashboard-bars">
      {bars.map((bar) => (
        <button
          className={`dashboard-bar${
            selected && selected !== bar.key ? ' faded' : ''
          }${selected === bar.key ? ' selected' : ''}`}
          key={bar.key}
          onClick={() => onSelect?.(bar.key)}
          type="button"
        >
          <span className="dashboard-bar-label" title={bar.label}>
            {bar.label}
          </span>
          <span className="dashboard-bar-track">
            <span
              className="dashboard-bar-fill"
              style={{
                background: bar.color,
                width: `${Math.max(1, (bar.value / max) * 100)}%`,
              }}
            />
          </span>
          <span className="dashboard-bar-value">{bar.text}</span>
          {bar.badge ? (
            <span
              className="dashboard-badge"
              style={{ background: bar.badge.color }}
            >
              {bar.badge.text}
            </span>
          ) : (
            <span />
          )}
        </button>
      ))}
    </div>
  );
};

/**
 * A funnel of the steps of selling, e.g. opportunities -> preliminary offers -> offers ->
 * approved, with the share of each step from the first one.
 */
export const Funnel = ({
  steps,
}: {
  steps: { color: string; label: string; text: string; value: number }[];
}): JSX.Element => {
  const max = Math.max(1, ...steps.map((step) => step.value));

  return (
    <div className="dashboard-funnel">
      {steps.map((step) => (
        <div className="dashboard-funnel-step" key={step.label}>
          <div
            className="dashboard-funnel-bar"
            style={{
              background: step.color,
              width: `${Math.max(8, (step.value / max) * 100)}%`,
            }}
          >
            <span>{step.text}</span>
          </div>
          <div className="dashboard-funnel-label">{step.label}</div>
        </div>
      ))}
    </div>
  );
};
